import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  Context,
  ReactNode,
} from "react";
import { IntlProvider } from "react-intl";
import { es, en, pt } from "../lang";

type LanguageContextType = {
  readysizelocale: string;
  messages: any;
  changeLanguage: (locale: string) => void;
};

const LanguageContext: Context<LanguageContextType> =
  createContext<LanguageContextType>({
    readysizelocale: "es", // default language is Spanish
    messages: es,
    changeLanguage: () => {},
  });

interface LanguageProviderProps {
  children: ReactNode;
  cultureLocale?: string;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  cultureLocale = "es-ES", // default culture
}) => {
  const getLocaleFromCulture = (culture: string) => {
    const [lang] = culture.split("-");
    return lang;
  };

  const [readysizelocale, setReadysizelocale] = useState<string>(
    getLocaleFromCulture(cultureLocale)
  );
  const [messages, setMessages] = useState<any>(() => {
    switch (getLocaleFromCulture(cultureLocale)) {
      case "en":
        return en;
      case "pt":
        return pt;
      case "es":
      default:
        return es;
    }
  });

  useEffect(() => {
    const storedLocale =
      localStorage.getItem("readysizelocale") ||
      getLocaleFromCulture(cultureLocale);
    setReadysizelocale(storedLocale);
    switch (storedLocale) {
      case "en":
        setMessages(en);
        break;
      case "pt":
        setMessages(pt);
        break;
      case "es":
      default:
        setMessages(es);
    }
  }, [cultureLocale]);

  const changeLanguage = (newLocale: string) => {
    localStorage.setItem("readysizelocale", newLocale);
    setReadysizelocale(newLocale);
    switch (newLocale) {
      case "en":
        setMessages(en);
        break;
      case "pt":
        setMessages(pt);
        break;
      case "es":
      default:
        setMessages(es);
    }
  };

  return (
    <LanguageContext.Provider
      value={{ readysizelocale, messages, changeLanguage }}
    >
      <IntlProvider locale={readysizelocale} messages={messages}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
export { LanguageContext };
